import type { ReactNode } from 'react';
import { EmptyState, ErrorState } from '@/components/States';

/** The slice of a TanStack query this component needs. */
export interface AsyncState<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  isFetching: boolean;
  refetch: () => unknown;
}

export interface AsyncSectionProps<T> {
  state: AsyncState<T>;
  /** Shown while the first request is in flight. */
  skeleton: ReactNode;
  /** Decides whether resolved data counts as "nothing to show". */
  isEmpty?: (data: T) => boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: ReactNode;
  errorTitle?: string;
  /**
   * Narrows resolved data before the empty check and render — used by the
   * report toolbar to apply search/filters to endpoints that return their
   * whole result set. Applied BEFORE [isEmpty] so filtering everything out
   * shows the empty state rather than an empty table.
   */
  select?: (data: T) => T;
  children: (data: T) => ReactNode;
}

/**
 * One place for the loading / error+retry / empty / data ladder every report
 * panel needs, so the whole portal behaves identically.
 */
export function AsyncSection<T>({
  state,
  skeleton,
  isEmpty,
  emptyTitle = 'Nothing to show',
  emptyDescription = 'There is no data for this filter yet.',
  emptyIcon,
  errorTitle,
  select,
  children,
}: AsyncSectionProps<T>) {
  if (state.isPending) return <>{skeleton}</>;

  if (state.isError || state.data === undefined) {
    return (
      <ErrorState
        error={state.error}
        title={errorTitle}
        onRetry={() => void state.refetch()}
        retrying={state.isFetching}
      />
    );
  }

  const data = select ? select(state.data) : state.data;

  if (isEmpty?.(data) === true) {
    return <EmptyState title={emptyTitle} description={emptyDescription} icon={emptyIcon} compact />;
  }

  return <>{children(data)}</>;
}
